import { Block } from '@ttab/elephant-api/newsdoc'
import escapeHTML from 'escape-html'
import { assignmentTypes } from '../../../assignmentTypes.js'
import type { AssignmentType } from '../../../assignmentTypes.js'
import { memoColumn, memoColumnOrder } from './columns.js'
import type { InsertedMemo } from './insertedDocument.js'
import { plainText } from './plainText.js'
import { splitTopNews } from './topNews.js'
import type {
  MemoAssignmentLine, MemoBlock, MemoSourceType, MemoTemplate, MemoVariant
} from './schema.js'

/**
 * One of the assignments the item's planning carries, in the order the planning
 * carries them. The title is what a memo prints; it is often empty, since an
 * assignment is not required to have one.
 *
 * The slugline is the assignment's own, which can differ from its planning's -
 * a sidebar is delivered under a slugline of its own. Absent when it has none.
 */
export interface MemoAssignment {
  type: AssignmentType
  title: string
  slugline?: string
}

/**
 * One selectable line in a memo, mapped from an index hit. `blockId` decides
 * which of the template's blocks the item is rendered under.
 *
 * `id` identifies this line, not the document behind it: a template that puts
 * one document in several of its blocks holds one item per block, each with its
 * own id, block and keyword. `documentId` is what they have in common - the
 * uuid of the document all of them came from, which is what says whether the
 * memo has taken a candidate at all. For an item that sits in one block only,
 * as most do, the two are the same string.
 */
export interface MemoItem {
  id: string
  documentId: string
  blockId: string
  sourceType: MemoSourceType
  headline: string
  sectionUuid?: string
  sectionTitle?: string
  newsvalue?: string
  slugline?: string
  description?: string
  publicDescription?: string
  /**
   * The assignments of the planning the item came from, absent for a source
   * that carries none. What a block's `assignmentLines` render from.
   */
  assignments?: MemoAssignment[]
  startTime?: string
  /** The day the item starts on, `YYYY-MM-DD` in the registry timezone. */
  startDate?: string
  /** The time of day it starts at, `HH.mm`, absent for a whole day item. */
  startTimeLabel?: string
  /**
   * The print layout keyword the editor picked for this item, e.g. `TOPP`. Only
   * ever set for items in a block that declares `keywords`, and always optional
   * - most items carry none.
   */
  keyword?: string
}

type Placeholders = Record<string, string>

const PLACEHOLDER = /\{(\w+)\}/g

/** A run of nothing but the punctuation a template puts between placeholders. */
const SEPARATOR = /^[\s–:-]+$/

/** One piece of a pattern: text the template wrote, or a placeholder to fill in. */
type Token = { literal: string } | { key: string }

/** A pattern as its literal text and its placeholders, in the order written. */
function tokenize(pattern: string): Token[] {
  const tokens: Token[] = []
  let index = 0

  for (const match of pattern.matchAll(PLACEHOLDER)) {
    if (match.index > index) {
      tokens.push({ literal: pattern.slice(index, match.index) })
    }

    tokens.push({ key: match[1] })
    index = match.index + match[0].length
  }

  if (index < pattern.length) {
    tokens.push({ literal: pattern.slice(index) })
  }

  return tokens
}

/**
 * Substitute `{placeholder}` occurrences. Returns undefined when the pattern
 * contains placeholders and every one of them resolves to empty, which is how
 * optional lines such as "Text: {slugline}: {description}" disappear for items
 * that have neither.
 *
 * A placeholder that resolves to empty takes one of the separators around it
 * with it, so that the punctuation the template wrote to stand between two
 * values is not left standing beside one: "Rubrik – " and "Text: : lindintervju"
 * are both a separator that no longer separates anything. The one after it goes
 * where there is one, since a pattern reads left to right - "Text: {slugline}:
 * {assignmentTitle}" without a slugline is "Text: lindintervju" - and otherwise
 * the one before, which is what clears the end of a line.
 *
 * Done while the line is still in pieces rather than by tidying the finished
 * string, because only then is a separator the template wrote distinguishable
 * from the same character inside a value. Trimming the ends took a headline's
 * opening pratminus with it - a quote is the most ordinary headline there is -
 * and a trailing colon, and it could not reach a separator left in the middle at
 * all. What a template wrote is the renderer's to remove; what the document says
 * is not.
 */
function interpolate(pattern: string, values: Placeholders): string | undefined {
  const tokens = tokenize(pattern)
  const dropped = new Set<number>()
  let seen = 0
  let resolved = 0

  tokens.forEach((token, index) => {
    if ('literal' in token) {
      return
    }

    seen += 1

    if (values[token.key]) {
      resolved += 1

      return
    }

    dropped.add(index)

    const after = tokens[index + 1]

    // Not one already dropped by the placeholder on its other side: a separator
    // separates two things, and removing it twice would close up the one join
    // it was still making.
    if (after && 'literal' in after && SEPARATOR.test(after.literal)
      && !dropped.has(index + 1)) {
      dropped.add(index + 1)

      return
    }

    const before = tokens[index - 1]

    if (before && 'literal' in before && SEPARATOR.test(before.literal)) {
      dropped.add(index - 1)
    }
  })

  if (seen > 0 && resolved === 0) {
    return undefined
  }

  const text = tokens
    .map((token, index) => {
      if (dropped.has(index)) {
        return ''
      }

      return 'literal' in token ? token.literal : values[token.key]
    })
    .join('')
    // Whitespace only, so that nothing a document wrote is ever taken off a
    // line: a description written over several lines becomes the one line a
    // memo has room for, and keeps every character of itself.
    .replace(/\s+/g, ' ')
    .trim()

  return text || undefined
}

function placeholdersFor(block: MemoBlock, item: MemoItem): Placeholders {
  return {
    headline: headlineFor(block, item),
    section: item.sectionTitle ? item.sectionTitle.toUpperCase() : '',
    slugline: item.slugline ?? '',
    description: item.description ?? '',
    publicDescription: item.publicDescription ?? '',
    time: item.startTimeLabel ?? '',
    keyword: item.keyword ?? ''
  }
}

function isEvent(item: MemoItem): boolean {
  return memoColumn(item.sourceType) === 'events'
}

/**
 * Whether an event goes without its title: the block names the sections that
 * print one and the event's is not among them. An event with no description
 * keeps it, since the title is then all there is to say what it is.
 */
function hidesTitle(block: MemoBlock, item: MemoItem): boolean {
  if (!block.eventTitleSections || !isEvent(item) || !item.publicDescription?.trim()) {
    return false
  }

  const section = item.sectionTitle ? sectionKey(item.sectionTitle) : ''

  return !block.eventTitleSections.some((entry) => sectionKey(entry) === section)
}

/** Trailing punctuation or symbol that already closes a title. */
const CLOSED_TITLE = /[\p{P}\p{S}]$/u

/**
 * The `{headline}` an item prints. An event title kept ahead of its description
 * gets a full stop, unless it already ends in punctuation or a symbol, so that
 * the two do not run together.
 */
function headlineFor(block: MemoBlock, item: MemoItem): string {
  if (hidesTitle(block, item)) {
    return ''
  }

  const headline = item.headline?.trim() ?? ''

  if (!block.eventTitleSections || !isEvent(item) || !item.publicDescription?.trim()) {
    return headline
  }

  if (!headline || CLOSED_TITLE.test(headline)) {
    return headline
  }

  return `${headline}.`
}

/**
 * A section title as it is compared: the datastore writes it, not the template,
 * so neither its casing nor its surrounding whitespace is worth insisting on.
 */
function sectionKey(section: string): string {
  return section.trim().toLocaleLowerCase()
}

/**
 * The groups a section grouped block renders, as `[heading, items]` pairs in
 * render order. An empty heading is a run of items with nothing above it.
 *
 * A block that names no sections orders them by first appearance among the
 * items - the order the editor selected/arranged them in - heads each with its
 * own title, and puts the items without a section last, ungrouped.
 */
function groupItems(block: MemoBlock, items: MemoItem[]): Array<[string, MemoItem[]]> {
  const groups = new Map<string, MemoItem[]>()

  for (const item of items) {
    const key = item.sectionTitle ?? ''
    const group = groups.get(key)

    if (group) {
      group.push(item)
    } else {
      groups.set(key, [item])
    }
  }

  if (block.sectionHeadings.length) {
    return namedGroups(block, groups)
  }

  const sections = [...groups.keys()].filter((section) => section)

  return [...sections, ...(groups.has('') ? [''] : [])]
    .map((section): [string, MemoItem[]] => [section, groups.get(section) ?? []])
}

/**
 * The groups of a block that names its sections: every named section that has
 * items, under the heading it is named with and in the order the block names
 * them, followed by whatever is left over.
 *
 * What is left over - sections the block does not name, and the items carrying
 * no section at all - is pooled under `otherSectionHeading` when the block
 * declares one, so that nothing ends up under a heading it does not belong to.
 * A block that declares none leaves them the plain shape: each section under its
 * own title in order of first appearance, the sectionless items unheaded last.
 */
function namedGroups(
  block: MemoBlock,
  groups: Map<string, MemoItem[]>
): Array<[string, MemoItem[]]> {
  const named: Array<[string, MemoItem[]]> = []
  const taken = new Set<string>()

  for (const { section, heading } of block.sectionHeadings) {
    const matches = [...groups.keys()].filter(
      (title) => !!title && sectionKey(title) === sectionKey(section)
    )

    if (!matches.length) {
      continue
    }

    for (const title of matches) {
      taken.add(title)
    }

    named.push([heading, matches.flatMap((title) => groups.get(title) ?? [])])
  }

  const leftover = [...groups.entries()].filter(([title]) => !taken.has(title))

  if (!block.otherSectionHeading) {
    return [
      ...named,
      ...leftover.filter(([title]) => !!title),
      ...leftover.filter(([title]) => !title)
    ]
  }

  const rest = leftover.flatMap(([, entries]) => entries)

  if (!rest.length) {
    return named
  }

  return [...named, [block.otherSectionHeading, rest]]
}

/** A `YYYY-MM-DD` day, which is what an item's `startDate` is. */
const DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Group a block's items by the day they start on, days in chronological order.
 * Items without a day go last, ungrouped, the way the sectionless ones do.
 *
 * An item whose day cannot be read is one of those. Only a day gets as far as
 * `resolveDayHeading`, which has nothing to name an unreadable one - it printed
 * "Invalid Date den Invalid Date" for a planning carried in as the
 * instant the index writes its date as. Where that day comes from is
 * `toMemoItems`' to get right; that a heading never says Invalid Date is this
 * function's.
 */
function groupByDay(items: MemoItem[]): Array<[string, MemoItem[]]> {
  const groups = new Map<string, MemoItem[]>()

  for (const item of items) {
    const day = item.startDate ?? ''
    const key = DAY.test(day) ? day : ''
    const group = groups.get(key)

    if (group) {
      group.push(item)
    } else {
      groups.set(key, [item])
    }
  }

  const days = [...groups.keys()].filter((day) => !!day).sort()

  return [...days, ...(groups.has('') ? [''] : [])]
    .map((day): [string, MemoItem[]] => [day, groups.get(day) ?? []])
}

/**
 * Items with a time of day before the whole day ones, each keeping the order
 * they came in - a partition rather than a sort, so that the order the editor
 * arranged them in survives.
 */
function timedFirst(items: MemoItem[]): MemoItem[] {
  return [
    ...items.filter((item) => !!item.startTimeLabel),
    ...items.filter((item) => !item.startTimeLabel)
  ]
}

/**
 * A run of items in time order, the ones with no time of day last - the order a
 * calendar listing is read in, and what a block asks for with `itemOrder`.
 *
 * `Array#sort` is stable, so items sharing a time of day - and the whole day
 * ones, sharing none - keep the order they were given.
 */
function byTime(items: MemoItem[]): MemoItem[] {
  return [...items].sort((a, b) => {
    const first = a.startTimeLabel ?? ''
    const second = b.startTimeLabel ?? ''

    if (!first) {
      return second ? 1 : 0
    }

    if (!second) {
      return -1
    }

    return first.localeCompare(second)
  })
}

/**
 * Resolve a day heading for one day, e.g. "Torsdag den 27 augusti 2026" or
 * "FREDAG". `{dayMonth}` is the date without its year, e.g. "27 augusti".
 *
 * `{dayName}` is the weekday as the locale writes it, `{DayName}` the same with
 * a capital first letter, for a heading that opens with it, and `{DAYNAME}` the
 * same in capitals, which is how the weekend PM's days are written. Uppercasing
 * is a placeholder of its own rather than something done to every day heading,
 * since a heading that reads as a sentence must not shout.
 *
 * The day is a `YYYY-MM-DD` key already expressed in the registry timezone, so
 * it is read back as a UTC instant and formatted as one. Letting the runtime
 * interpret it instead would name the day before west of UTC.
 */
function resolveDayHeading(pattern: string, day: string, localeCode: string): string {
  const [year, month, date] = day.split('-').map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, date))
  const options: Intl.DateTimeFormatOptions = { timeZone: 'UTC' }
  const dayName = parsed.toLocaleDateString(localeCode, { ...options, weekday: 'long' })
  const capitalized = dayName.charAt(0).toLocaleUpperCase(localeCode) + dayName.slice(1)

  return pattern
    .replaceAll('{dayName}', dayName)
    .replaceAll('{DayName}', capitalized)
    .replaceAll('{DAYNAME}', dayName.toLocaleUpperCase(localeCode))
    .replaceAll('{longDate}', parsed.toLocaleDateString(localeCode, {
      ...options,
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }))
    .replaceAll('{dayMonth}', parsed.toLocaleDateString(localeCode, {
      ...options,
      day: 'numeric',
      month: 'long'
    }))
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * The label of the types an item has. They are taken apart first, so a picture
 * assignment beside a picture/video one is picture and video, and a combination
 * the entry labels - "Bild/video" - is labelled as one rather than as its parts
 * joined, which read "Bild/Bild/Video".
 */
function labelFor(entry: MemoAssignmentLine, present: AssignmentType[]): string {
  const parts = [...new Set(present.flatMap((type) => type.split('/')))].sort()
  const combined = assignmentTypes.find((type) => type === parts.join('/'))
  const combinedLabel = combined ? entry.labels[combined] : undefined

  if (combinedLabel) {
    return combinedLabel
  }

  return [...new Set(
    present.map((type) => entry.labels[type]).filter((label) => !!label)
  )].join('/')
}

/**
 * The lines one `assignmentLines` entry contributes for an item: one per
 * assignment of the first of the entry's types the item has, labelled with
 * every one of them it has.
 *
 * An item with none of the entry's types renders nothing for it: a memo promises
 * only the assignments the planning carries, so a removed text assignment must
 * not survive as a bare "Text: {slugline}".
 *
 * Untitled assignments render no line of their own but do count towards the
 * label: an untitled video assignment beside a titled picture one still makes
 * the line read "Bild/video".
 *
 * `{slugline}` on an assignment's line is the assignment's own, falling back on
 * the planning's for an assignment that has none.
 */
function renderAssignmentLine(
  entry: MemoAssignmentLine,
  item: MemoItem,
  values: Placeholders
): string[] {
  const assignments = item.assignments ?? []
  const present = entry.types.filter((type) => (
    assignments.some((assignment) => assignment.type === type)
  ))

  if (!present.length) {
    return []
  }

  const assignmentLabel = labelFor(entry, present)

  return assignments
    .filter((assignment) => assignment.type === present[0] && assignment.title)
    .map((assignment) => interpolate(entry.pattern, {
      ...values,
      slugline: assignment.slugline || values.slugline,
      assignmentLabel,
      assignmentTitle: withFullStop(assignment.title)
    }))
    .filter((line): line is string => !!line)
}

/**
 * An assignment title ends a sentence in the memo, so one ending in a letter or a
 * digit gets a full stop. One ending in anything else - punctuation, a quote, a
 * parenthesis - is left as written.
 */
function withFullStop(title: string): string {
  const trimmed = title.trim()

  if (/[\p{L}\p{N}]$/u.test(trimmed)) {
    return `${trimmed}.`
  }

  return trimmed
}

/** The format an item renders with: the block's own for its kind, or `item`. */
function formatFor(block: MemoBlock, item: MemoItem): MemoBlock['item'] {
  if (isEvent(item) && block.eventItem) {
    return block.eventItem
  }

  if (item.sourceType === 'article' && block.articleItem) {
    return block.articleItem
  }

  return block.item
}

function renderItem(block: MemoBlock, item: MemoItem): MemoLine[] {
  const values = placeholdersFor(block, item)
  const format = formatFor(block, item)
  const lines: string[] = []
  const headline = interpolate(format.headline, values)
  const headlineRole = headline ? format.headlineRole : undefined

  if (headline) {
    lines.push(headline)
  }

  for (const line of format.lines) {
    const rendered = interpolate(line, values)

    if (rendered) {
      lines.push(rendered)
    }
  }

  for (const entry of format.assignmentLines) {
    lines.push(...renderAssignmentLine(entry, item, values))
  }

  const event = isEvent(item)

  return lines.map((text, index) => ({
    text,
    ...(index === 0 && headlineRole ? { role: headlineRole } : {}),
    itemId: item.id,
    blockId: block.id,
    ...(event ? { event } : {})
  }))
}

/**
 * A rendered line and the newsdoc text role it should carry. `itemId` names
 * the `MemoItem` it was rendered from, so a consumer can regroup an item's
 * lines - undefined for structural lines (headings, preamble) that no item
 * owns. `blockId` names the block the line was rendered under - every line
 * carries it except the title and the template's own preamble/postamble, which
 * belong to no block - so a consumer can tell where one block's content ends
 * and the next begins. `blockHeading` marks the line
 * rendered from a block's own `heading`, telling it apart from the subheadings
 * a grouped block emits per section. `documentId` names the inserted document a
 * line was taken from, the way `itemId` names an item. Those four are UI-only
 * metadata, ignored when a line is turned into saved newsdoc content.
 *
 * `bold` says the line's text is emphasised with `core/bold` from end to end,
 * and is not UI-only: it is what the saved block is wrapped in - see
 * `renderMemo`.
 *
 * A memo this renderer writes is one heading deep - the title, as `heading-1` -
 * and what divides the body below it does so by standing on a line of its own
 * rather than by being formatted: a block's heading, a section subheading and a
 * day are all plain text. It emphasises nothing it writes itself; only a line
 * read from an inserted PM can carry `heading-2` or `bold`, since such a line is
 * passed through as it was written.
 *
 * `block` is the newsdoc block an inserted line was read from. It is what the
 * saved document gets for that line, verbatim, so that formatting a flat line
 * cannot express - a link, half a sentence in bold - survives being carried
 * from one PM into another. `text` is then the plain text of it, for the
 * preview to show. `gapBefore` says a blank line stood above it in that PM,
 * which the saved document keeps and the preview does not show.
 *
 * `postamble` marks a line of the template's sign-off, whose lines are one
 * entry and are saved without empty paragraphs between them.
 *
 * `eventsStart` marks the first line of a block's events where material comes
 * before them, which both the preview and the saved memo set apart.
 *
 * `eventsHeading` marks a line of a block's `eventsHeading`, whose lines are
 * one entry, saved without empty paragraphs between them.
 *
 * `event` marks a line rendered from an event. Events follow one another
 * directly, one per line, without empty paragraphs between them.
 *
 * `sectionHeading` marks a section's heading in a block grouped by section,
 * which the section's first item follows directly.
 */
export interface MemoLine {
  text: string
  role?: 'heading-1' | 'heading-2' | 'preamble'
  bold?: boolean
  itemId?: string
  blockId?: string
  blockHeading?: boolean
  documentId?: string
  block?: Block
  gapBefore?: boolean
  postamble?: boolean
  eventsStart?: boolean
  eventsHeading?: boolean
  event?: boolean
  sectionHeading?: boolean
}

/**
 * Rendering options. The locale names the weekdays and months of a day grouped
 * block's subheadings, and defaults to Swedish like the rest of the memos. The
 * variant is the shape a template that comes in more than one is rendered as,
 * and supplies the memo's intro in place of the intro block's preamble.
 * `inserted` is the already written PMs the editor folded in, each rendered
 * whole at the end of the block it names, in the order they were picked.
 *
 * `sectionTitle` is the section the memo is written for, the way it is for a
 * title and a slugline - and it is the title's naming it that keeps the body
 * from repeating it. See `withoutWrittenSection`.
 */
export interface MemoRenderOptions {
  localeCode?: string
  variant?: MemoVariant
  inserted?: InsertedMemo[]
  sectionTitle?: string
}

/** A block whose text is bold from end to end, which is what `bold` can say. */
const BOLD_WRAPPED = /^<strong>(?:(?!<\/strong>)[\s\S])*<\/strong>$/

/**
 * The roles an inserted line keeps. The source PM's heading-1 is dropped before
 * it ever gets here, and any other role a block might carry means nothing in a
 * memo - such a line is body text.
 */
const INSERTED_ROLES = ['heading-2', 'preamble'] as const

/**
 * One inserted block as a line: its plain text for the preview, its role and
 * emphasis for how that text is shown, and the block itself for what is saved.
 * An empty block is no line - it is the gap above the next one, see
 * `insertedLines`.
 */
function toInsertedLine(document: InsertedMemo, block: Block): MemoLine | undefined {
  const html = block.data.text ?? ''
  const text = plainText(html)

  if (!text) {
    return undefined
  }

  const role = INSERTED_ROLES.find((known) => known === block.role)

  return {
    text,
    ...(role ? { role } : {}),
    // A heading is already set apart by being one, so the emphasis only says
    // anything for a line of body text.
    ...(!role && BOLD_WRAPPED.test(html.trim()) ? { bold: true } : {}),
    documentId: document.id,
    blockId: document.blockId,
    block
  }
}

/**
 * Every inserted document's lines, in the order the editor picked them. A line
 * with blank blocks between it and the one above it in its PM carries
 * `gapBefore`, however many there were; blank blocks at either end carry none.
 */
function insertedLines(documents: InsertedMemo[]): MemoLine[] {
  return documents.flatMap((document) => {
    const lines: MemoLine[] = []
    let gap = false

    for (const block of document.blocks) {
      const line = toInsertedLine(document, block)

      if (!line) {
        gap = lines.length > 0
        continue
      }

      lines.push(gap ? { ...line, gapBefore: true } : line)
      gap = false
    }

    return lines
  })
}

/**
 * Render a run of a block's items, which arrive sorted by column.
 *
 * A block ordering its items by time is reordered here rather than where its
 * items are grouped, so that one run - a section within a day, a day, or the
 * whole block - is ordered without knowing which grouping produced it.
 *
 * A block that prints its events' times is read as a calendar, so it is
 * ordered by time too, the whole day events last.
 */
function renderItems(block: MemoBlock, items: MemoItem[]): MemoLine[] {
  const ordered = ordersByTime(block) ? byTime(items) : items

  return ordered.flatMap((item) => renderItem(block, item))
}

/**
 * Whether a block lists its items by time of day, whatever order the editor
 * gave them - the whole day ones last, in the order they came in.
 */
export function ordersByTime(block: MemoBlock): boolean {
  return block.itemOrder === 'time' || printsTime(block)
}

/** Whether the format a block renders its events with prints their time. */
function printsTime(block: MemoBlock): boolean {
  const format = block.eventItem ?? block.item

  return [format.headline, ...format.lines].some((pattern) => pattern.includes('{time}'))
}

/**
 * A block's items: its top news under their own subheading, then the material
 * and the events after it, each part grouped the way the block asks for - its
 * own sections, days and all. The events open with the block's `eventsHeading`,
 * and where they follow material their first line carries `eventsStart`, which
 * sets them apart.
 */
function renderBlockItems(
  block: MemoBlock,
  blockItems: MemoItem[],
  localeCode: string
): MemoLine[] {
  const { top, rest: others } = splitTopNews(block, blockItems)
  const materialItems = others.filter((item) => !isEvent(item))
  const material = [
    ...renderTopNews(block, top),
    ...renderGroupedItems(block, materialItems, localeCode)
  ]
  const events = renderGroupedItems(block, others.filter(isEvent), localeCode)
  const heading = events.length
    ? block.eventsHeading.map((text): MemoLine => ({
      text,
      blockId: block.id,
      eventsHeading: true
    }))
    : []
  const [first, ...rest] = [...heading, ...events]

  if (!first) {
    return material
  }

  if (!material.length) {
    return [first, ...rest]
  }

  return [...material, { ...first, eventsStart: true }, ...rest]
}

/** A block's top news under its subheading, plain body text like a section's. */
function renderTopNews(block: MemoBlock, top: MemoItem[]): MemoLine[] {
  if (!block.topNews || !top.length) {
    return []
  }

  return [
    { text: block.topNews.heading, blockId: block.id, sectionHeading: true },
    ...renderItems(block, top)
  ]
}

/** A run of a block's items, grouped the way the block asks for. */
function renderGroupedItems(
  block: MemoBlock,
  blockItems: MemoItem[],
  localeCode: string
): MemoLine[] {
  const lines: MemoLine[] = []

  if (block.grouping === 'section') {
    for (const [heading, grouped] of groupItems(block, blockItems)) {
      if (heading) {
        // A section is a run of body text within the block rather than a
        // section of the memo, so it is neither a heading of its own nor
        // emphasised - see `renderBlockBody`.
        lines.push({ text: heading, blockId: block.id, sectionHeading: true })
      }

      lines.push(...renderItems(block, grouped))
    }

    return lines
  }

  if (block.grouping === 'day') {
    for (const [day, grouped] of groupByDay(blockItems)) {
      if (day && block.dayHeading) {
        // A day is a run of body text within the block rather than a section of
        // the memo, so it is plain body text rather than a heading of its own.
        lines.push({
          text: resolveDayHeading(block.dayHeading, day, localeCode),
          blockId: block.id
        })
      }

      lines.push(...renderItems(block, timedFirst(grouped)))
    }

    return lines
  }

  lines.push(...renderItems(block, blockItems))

  return lines
}

/**
 * A block's own heading, its items grouped the way the block asks for, and the
 * PMs the editor folded into it. Shared by the two shapes a memo comes in - one
 * run of blocks, or one run of blocks per day - which differ in what surrounds a
 * block, not in what a block renders as.
 *
 * An inserted PM goes last, after everything the block's own items rendered,
 * whichever way they were grouped: it is a whole PM restated inside the block
 * rather than one more item to be sorted among them.
 */
function renderBlockBody(
  block: MemoBlock,
  blockItems: MemoItem[],
  localeCode: string,
  inserted: InsertedMemo[] = []
): MemoLine[] {
  const lines: MemoLine[] = []

  if (block.heading) {
    // The memo's title is the one heading it has. A block's heading divides the
    // body of it - "UPPSLAG", then "ENKELSIDAN" - by standing on a line of its
    // own, so it is plain body text and not a heading of its own.
    lines.push({
      text: block.heading,
      blockId: block.id,
      blockHeading: true
    })
  }

  lines.push(...renderBlockItems(block, blockItems, localeCode))
  lines.push(...insertedLines(inserted))

  return lines
}

/**
 * The items as a memo written for one section renders them: sectionless, that
 * section being what the memo's own title already names. Which drops it off the
 * end of a headline that names one - "Toppmötet inleds", not "Toppmötet inleds
 * – UTRIKES" - and collapses the grouping it would have got into one unheaded
 * run, a heading per section being nothing to tell apart when there is one.
 *
 * Only for a template that names itself per section, since that is what says
 * the section is in the title: the candidate columns of any other template can
 * be narrowed to one section too, and dropping the headings it groups them
 * under is then a memo quietly rendered as a shape nobody asked for.
 *
 * An item carrying another section keeps it. Only the section the memo is
 * written for has already been said.
 */
function withoutWrittenSection(
  template: MemoTemplate,
  items: MemoItem[],
  sectionTitle: string | undefined
): MemoItem[] {
  if (!template.planning.section || !sectionTitle) {
    return items
  }

  const written = sectionKey(sectionTitle)

  return items.map((item) => {
    if (!item.sectionTitle || sectionKey(item.sectionTitle) !== written) {
      return item
    }

    return { ...item, sectionTitle: undefined }
  })
}

/** A block's items, in candidate column order. */
function itemsOfBlock(block: MemoBlock, items: MemoItem[]): MemoItem[] {
  return items
    .filter((item) => item.blockId === block.id)
    .sort((a, b) => (
      memoColumnOrder.indexOf(memoColumn(a.sourceType))
      - memoColumnOrder.indexOf(memoColumn(b.sourceType))
    ))
}

/**
 * Render a memo whose outer axis is the day rather than the block: a heading
 * per day, and within it every block that has something on that day.
 *
 * That is the weekend PM's shape - "FREDAG", then that Friday's top news and
 * the rest of its material by section, then "LÖRDAG" and the same again - as
 * opposed to a block that groups its own items by day, which renders each block
 * whole and splits it internally.
 *
 * Block preambles are left out here: the memo's intro is rendered once above,
 * and repeating any other block's intro line under every day would read as a
 * template stuttering.
 */
function renderDayGrouped(
  template: MemoTemplate,
  items: MemoItem[],
  localeCode: string
): MemoLine[] {
  const lines: MemoLine[] = []

  for (const [day, dayItems] of groupByDay(items)) {
    if (day && template.dayHeading) {
      lines.push({ text: resolveDayHeading(template.dayHeading, day, localeCode) })
    }

    for (const block of template.blocks) {
      const blockItems = itemsOfBlock(block, dayItems)

      if (!blockItems.length) {
        continue
      }

      lines.push(...renderBlockBody(block, blockItems, localeCode))
    }
  }

  return lines
}

/**
 * The lines the memo opens with, below the title.
 *
 * Three things can supply them, in this order: the variant, for a template that
 * comes in more than one shape; the template's own `preamble`, for a memo whose
 * opening line is one line about the whole memo; and otherwise the preamble of
 * the block that leads. All three stand in for the leading block's own text
 * rather than being added to it - which is why the block loop below never
 * renders that block's preamble a second time. The template's own belongs to no
 * block; the other two are the leading block's line and carry its id.
 *
 * A memo whose only content is a PM inserted under no block of this template has
 * no leading block at all, and then only the template's own opening line has
 * anything to say: the first and the third of the three are a block's line,
 * written for content that is not there.
 */
function renderIntro(
  template: MemoTemplate,
  introBlock: MemoBlock | undefined,
  variant: MemoVariant | undefined
): MemoLine[] {
  const intro = (text: string): MemoLine => ({ text, role: 'preamble' })

  if (variant?.preamble.length && introBlock) {
    return variant.preamble.map((text) => ({ ...intro(text), blockId: introBlock.id }))
  }

  if (template.preamble.length) {
    return template.preamble.map(intro)
  }

  if (!introBlock) {
    return []
  }

  return introBlock.preamble.map((text) => ({ ...intro(text), blockId: introBlock.id }))
}

/**
 * Render a memo to structured lines.
 *
 * Items are rendered under the template block they are assigned to, print
 * before material before events - see `memoColumns`, which is a coarser split
 * than the columns the creation dialog lists candidates in - and in the order
 * given within each run. Blocks without items are omitted entirely, preamble
 * and heading included.
 *
 * The memo's intro, right after the title, is the preamble of whichever block
 * ends up first (in template order) with content, unless the template - or the
 * variant it is rendered as - declares one of its own. A memo with no content in
 * any block gets no intro. See `renderIntro`.
 *
 * The template's postamble closes the memo off, below every block. A memo that
 * rendered nothing but its title gets none, the same way it gets no intro.
 *
 * An inserted PM is content of the block it names, rendered whole at the end of
 * it. That is what makes a memo of nothing but a restated PM still open with the
 * line the block it was put in opens with - a morning PM carrying the night's
 * five and nothing else says "God morgon!" first, the way it would if those five
 * had been picked out of the candidate columns one by one.
 *
 * A PM naming a block this template does not have - and every PM handed to a day
 * grouped memo, whose blocks recur under each day - is rendered between the
 * intro and the blocks, which is where every insert used to go. Nothing in the
 * creation dialog can produce either; a renderer that quietly dropped content
 * would be the worse answer if something ever did.
 */
export function renderMemoLines(
  template: MemoTemplate,
  items: MemoItem[],
  title: string,
  options: MemoRenderOptions = {}
): MemoLine[] {
  const localeCode = options.localeCode ?? 'sv-SE'
  const lines: MemoLine[] = [{ text: title, role: 'heading-1' }]
  const inserted = options.inserted ?? []
  const blockIds = new Set(template.blocks.map((block) => block.id))
  const rendered = withoutWrittenSection(template, items, options.sectionTitle)

  // A day grouped memo renders every block once per day, so a PM inserted into
  // one of them would be restated under each day the memo covers. There, and for
  // a PM naming a block this template has not got, it goes where every insert
  // used to: once, between the intro and the blocks.
  const placed = template.dayGrouping
    ? []
    : inserted.filter((entry) => blockIds.has(entry.blockId))
  const unplaced = inserted.filter((entry) => !placed.includes(entry))
  const insertedIn = (blockId: string) => placed.filter((entry) => entry.blockId === blockId)

  // The intro is whichever block ends up first, in template order, with
  // content - its own preamble is shown right after the title. An inserted PM
  // is that block's content as much as its items are. A memo with no content in
  // any block opens with no intro at all.
  const introBlock = template.blocks.find((block) => (
    rendered.some((item) => item.blockId === block.id) || !!insertedIn(block.id).length
  ))

  if (introBlock || unplaced.length) {
    lines.push(...renderIntro(template, introBlock, options.variant))
  }

  lines.push(...insertedLines(unplaced))

  if (template.dayGrouping) {
    lines.push(...renderDayGrouped(template, rendered, localeCode))
  } else {
    for (const block of template.blocks) {
      const blockItems = itemsOfBlock(block, rendered)
      const blockInserted = insertedIn(block.id)

      if (!blockItems.length && !blockInserted.length) {
        continue
      }

      // The intro block's preamble was already shown above - do not repeat it.
      // Mid-memo it is body text: a memo has one preamble, see `withOnePreamble`.
      if (block.id !== introBlock?.id && block.preamble.length) {
        lines.push(...block.preamble.map((text) => ({ text, blockId: block.id })))
      }

      lines.push(...renderBlockBody(block, blockItems, localeCode, blockInserted))
    }
  }

  // The postamble signs the memo off - who to call, where TT's lists are, a note
  // to the newsroom. A memo that rendered nothing but its title has nothing to
  // sign off, so it is left out there, the way the intro and every empty block are.
  if (lines.length > 1) {
    lines.push(...template.postamble.map((text) => ({ text, postamble: true })))
  }

  return withOnePreamble(lines)
}

/**
 * The lines with only the first preamble keeping its role. The publish pipeline
 * takes the first preamble as the ingress, so a later one - an inserted PM's own
 * intro, say - is saved as the body text it ends up as anyway.
 */
function withOnePreamble(lines: MemoLine[]): MemoLine[] {
  const first = lines.findIndex((line) => line.role === 'preamble')

  return lines.map((line, index) => {
    if (line.role !== 'preamble' || index === first) {
      return line
    }

    const { role: _role, ...bodyText } = line

    if (!bodyText.block) {
      return bodyText
    }

    return { ...bodyText, block: { ...bodyText.block, role: '' } }
  })
}

/**
 * Render a memo to plain text lines - `renderMemoLines` with the roles, blocks
 * and ids dropped. Nothing but the tests reads a memo this flatly: the preview
 * needs the roles to show a line as what it will be saved as, and the created
 * document needs the blocks.
 */
export function renderMemoText(
  template: MemoTemplate,
  items: MemoItem[],
  title: string,
  options: MemoRenderOptions = {}
): string[] {
  return renderMemoLines(template, items, title, options).map((line) => line.text)
}

/**
 * Render a memo to newsdoc content blocks, ready to be handed to
 * `editorialInfoDocumentTemplate` as `payload.content`.
 *
 * A `core/text` block's text is html, so a line this renderer writes is escaped
 * on the way in - the way the editor's own serialization escapes what is typed
 * into it. A headline reading "tst-gvl-12-1-&-test" is otherwise saved with a
 * bare ampersand in it, which the repository rejects as an unterminated entity.
 * Besides that escaping, the only markup a memo's own line carries is the
 * `<strong>` an emphasised one is wrapped in end to end; everything else the
 * memo writes is unemphasised body text.
 *
 * A line taken from an inserted PM already is a block, and is passed through as
 * the one it came from - escaped when it was written - so the memo says what
 * that PM said, down to the markup within a line.
 *
 * The entries of the memo are set apart by an empty paragraph - see
 * `separates`. The preview shows none of them: it separates its rows itself.
 * A template with `blankAfterTitle` also gets one right below the title.
 */
export function renderMemo(
  template: MemoTemplate,
  items: MemoItem[],
  title: string,
  options: MemoRenderOptions = {}
): Block[] {
  const lines = renderMemoLines(template, items, title, options)
  const listed = listedBlockIds(template)

  return lines.flatMap((line, index) => {
    const previous = lines[index - 1]
    const block = toBlock(line)

    if (line.role === 'heading-1' && template.blankAfterTitle) {
      return [block, emptyParagraph()]
    }

    if (previous && separates(previous, line, listed)) {
      return [emptyParagraph(), block]
    }

    return [block]
  })
}

function toBlock(line: MemoLine): Block {
  if (line.block) {
    return line.block
  }

  return Block.create({
    type: 'core/text',
    data: { text: emphasised(escapeHTML(line.text), line.bold) },
    ...(line.role ? { role: line.role } : {})
  })
}

function emptyParagraph(): Block {
  return Block.create({ type: 'core/text', data: { text: '' } })
}

/**
 * The blocks whose items are one line each - "Nattens fem", the print PM, a
 * calendar - which read as a list and are left unspaced within a run.
 */
function listedBlockIds(template: MemoTemplate): Set<string> {
  return new Set(template.blocks
    .filter((block) => !block.item.lines.length && !block.item.assignmentLines.length)
    .map((block) => block.id))
}

/**
 * Whether an empty paragraph goes between two neighbouring lines.
 *
 * Every entry of a memo stands apart from the next: a preamble line, a heading,
 * a selected item, an inserted PM, the sign-off. Within an entry nothing does -
 * an item's headline, slugline and "Bild:" lines are one entry, and an inserted
 * PM keeps only the gaps it was written with, and the sign-off's lines follow
 * one another directly, as do an events heading's, the events themselves and a
 * section heading and its first item. The exceptions are the
 * title, which the line below it follows directly, and a listed block, whose
 * heading and items read as one list - see `listedBlockIds`. Even a list is
 * split where its events begin.
 */
function separates(previous: MemoLine, line: MemoLine, listed: Set<string>): boolean {
  if (previous.role === 'heading-1') {
    return false
  }

  if (previous.postamble && line.postamble) {
    return false
  }

  if (previous.eventsHeading && line.eventsHeading) {
    return false
  }

  if (line.eventsStart) {
    return true
  }

  if (previous.event && line.event) {
    return false
  }

  if (previous.sectionHeading && line.itemId) {
    return false
  }

  if (previous.blockHeading && !!previous.blockId && listed.has(previous.blockId)) {
    return false
  }

  if (line.documentId && line.documentId === previous.documentId) {
    return !!line.gapBefore
  }

  if (line.itemId && line.itemId === previous.itemId) {
    return false
  }

  const inList = !!line.itemId && !!previous.itemId
    && !!line.blockId && line.blockId === previous.blockId
    && listed.has(line.blockId)

  return !inList
}

/**
 * A line's html, wrapped in `core/bold` from end to end where the line is
 * emphasised. The wrapping goes outside the escaping, so that a `<` the memo
 * itself wrote stays the entity it was escaped to and only the tags this adds
 * are markup.
 */
function emphasised(text: string, bold: boolean | undefined): string {
  if (!bold) {
    return text
  }

  return `<strong>${text}</strong>`
}
